import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import PersonaAvatar from './PersonaAvatar'

// ─── Types ──────────────────────────────────────────────────────

interface PersonaInfo {
  name: string
  display_name: string
  role: string
  description: string
  color: string
  avatar_image?: string | null
}

interface PopupState {
  personaName: string
  anchorRect: DOMRect
}

interface PersonaPopupContextValue {
  showPopup: (personaName: string, anchorEl: HTMLElement) => void
  hidePopup: () => void
  hoverProps: (personaName: string) => {
    onMouseEnter: (e: React.MouseEvent) => void
    onMouseLeave: () => void
    style: { cursor: string }
  }
}

const PersonaPopupContext = createContext<PersonaPopupContextValue>({
  showPopup: () => {},
  hidePopup: () => {},
  hoverProps: () => ({ onMouseEnter: () => {}, onMouseLeave: () => {}, style: { cursor: 'pointer' } }),
})

export const usePersonaPopup = () => useContext(PersonaPopupContext)

// ─── Provider ───────────────────────────────────────────────────

export function PersonaPopupProvider({ children }: { children: ReactNode }) {
  const [popup, setPopup] = useState<PopupState | null>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showPopup = useCallback((personaName: string, anchorEl: HTMLElement) => {
    const rect = anchorEl.getBoundingClientRect()
    if (leaveTimerRef.current) { clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null }
    setPopup({ personaName, anchorRect: rect })
  }, [])

  const hidePopup = useCallback(() => {
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null }
    leaveTimerRef.current = setTimeout(() => setPopup(null), 800)
  }, [])

  const hoverProps = useCallback((personaName: string) => ({
    onMouseEnter: (e: React.MouseEvent) => {
      if (leaveTimerRef.current) { clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null }
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
      const target = e.currentTarget as HTMLElement
      hoverTimerRef.current = setTimeout(() => showPopup(personaName, target), 400)
    },
    onMouseLeave: () => {
      if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null }
      hidePopup()
    },
    style: { cursor: 'pointer' as const },
  }), [showPopup, hidePopup])

  // Dismiss on click outside
  useEffect(() => {
    if (!popup) return
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopup(null)
      }
    }
    const timer = setTimeout(() => document.addEventListener('click', handler), 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('click', handler)
    }
  }, [popup])

  // Dismiss on Escape
  useEffect(() => {
    if (!popup) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setPopup(null) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [popup])

  // Fetch persona list (cached)
  const { data: personas } = useQuery<PersonaInfo[]>({
    queryKey: ['persona-popup-list'],
    queryFn: async () => {
      const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
      const res = await fetch('/api/personas', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      return res.json()
    },
    staleTime: 60_000,
    enabled: !!popup,
  })

  // Fetch awake status (cached)
  const { data: statusData } = useQuery<Record<string, { awake: boolean }>>({
    queryKey: ['persona-popup-status'],
    queryFn: async () => {
      const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
      const res = await fetch('/api/personas/status', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      return res.json()
    },
    staleTime: 30_000,
    enabled: !!popup,
  })

  // Fetch XP/growth data (cached)
  const { data: xpData } = useQuery<{ personas: Array<{ name: string; total_xp: number; level: string; dimensions: Record<string, number> }> }>({
    queryKey: ['persona-popup-xp'],
    queryFn: async () => {
      const res = await fetch('/api/benchmark/personas')
      if (!res.ok) throw new Error('benchmark fetch failed')
      return res.json()
    },
    staleTime: 60_000,
    enabled: !!popup,
  })

  const persona = popup ? personas?.find(p => p.name.toLowerCase() === popup.personaName.toLowerCase()) : null

  return (
    <PersonaPopupContext.Provider value={{ showPopup, hidePopup, hoverProps }}>
      {children}
      {popup && persona && (
        <PopupBubble
          ref={popupRef}
          persona={persona}
          awake={statusData?.[popup.personaName]?.awake ?? statusData?.[popup.personaName.toLowerCase()]?.awake ?? false}
          anchorRect={popup.anchorRect}
          xp={xpData?.personas?.find(p => p.name.toLowerCase() === popup.personaName.toLowerCase())}
          onPopupEnter={() => { if (leaveTimerRef.current) { clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null } }}
          onPopupLeave={hidePopup}
        />
      )}
    </PersonaPopupContext.Provider>
  )
}

// ─── Popup Bubble ───────────────────────────────────────────────

import { forwardRef } from 'react'

interface XpInfo {
  name: string
  total_xp: number
  level: string
  dimensions: Record<string, number>
}

const DIMENSION_META = [
  { key: 'competence', label: 'COM', color: '#E8935A' },
  { key: 'social', label: 'SOC', color: '#6FB8D6' },
  { key: 'creative', label: 'CRE', color: '#B265D6' },
  { key: 'wisdom', label: 'WIS', color: '#E6C85A' },
  { key: 'service', label: 'SRV', color: '#5AC884' },
  { key: 'identity', label: 'IDN', color: '#D65A93' },
]

const LEVEL_COLORS: Record<string, string> = {
  Newcomer: '#6B7280', Contributor: '#6FB8D6', Citizen: '#B265D6', Steward: '#E8935A', Elder: '#E6C85A',
}

const PopupBubble = forwardRef<HTMLDivElement, {
  persona: PersonaInfo
  awake: boolean
  anchorRect: DOMRect
  xp?: XpInfo
  onPopupEnter?: () => void
  onPopupLeave?: () => void
}>(function PopupBubble({ persona, awake, anchorRect, xp, onPopupEnter, onPopupLeave }, ref) {
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const innerRef = useRef<HTMLDivElement>(null)

  // Viewport-aware positioning
  useEffect(() => {
    const el = innerRef.current
    if (!el) return
    const gap = 8
    const popupRect = el.getBoundingClientRect()
    let top = anchorRect.bottom + gap
    let left = anchorRect.left

    // Flip up if near bottom
    if (top + popupRect.height > window.innerHeight - 16) {
      top = anchorRect.top - popupRect.height - gap
    }
    // Shift left if near right edge
    if (left + popupRect.width > window.innerWidth - 16) {
      left = window.innerWidth - popupRect.width - 16
    }
    // Don't go off left edge
    if (left < 16) left = 16

    setPos({ top, left })
  }, [anchorRect])

  const homeUrl = `/neighborhood/${persona.name}`

  return (
    <div
      ref={(node) => {
        (innerRef as any).current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) (ref as any).current = node
      }}
      className="persona-popup"
      onMouseEnter={onPopupEnter}
      onMouseLeave={onPopupLeave}
      onClick={() => { window.location.href = `/evolve/persona/${persona.name}` }}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex: 9999,
        width: 280,
        borderRadius: 12,
        background: 'var(--bg-surface, #1a1a24)',
        border: '1px solid var(--border, #30363d)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        overflow: 'hidden',
        animation: 'personaPopupEnter 180ms ease-out',
        pointerEvents: 'auto',
        cursor: 'pointer',
      }}
    >
      {/* Accent bar */}
      <div style={{ height: 3, background: persona.color }} />

      {/* Body */}
      <div style={{ padding: '12px 14px' }}>
        {/* Header: avatar + name + role */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <PersonaAvatar name={persona.name} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary, #e6edf3)' }}>
              {persona.display_name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted, #8b949e)', marginTop: 1 }}>
              {persona.role}
            </div>
          </div>
        </div>

        {/* Status + Level */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: awake ? '#3fb950' : '#484f58',
              boxShadow: awake ? '0 0 6px rgba(63,185,80,0.5)' : 'none',
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--text-muted, #8b949e)' }}>
            {awake ? 'Awake' : 'Sleeping'}
          </span>
          {xp && (
            <>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 2px' }}>·</span>
              <span style={{
                fontSize: 10, padding: '1px 6px', borderRadius: 6,
                background: `${LEVEL_COLORS[xp.level] || '#6B7280'}18`,
                color: LEVEL_COLORS[xp.level] || '#6B7280',
                fontWeight: 700, textTransform: 'uppercase',
              }}>
                {xp.level}
              </span>
              <span style={{ fontSize: 11, color: '#856FC8', fontWeight: 700, marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
                {xp.total_xp.toLocaleString()} XP
              </span>
            </>
          )}
        </div>

        {/* Dimension bars */}
        {xp && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10 }}>
            {DIMENSION_META.map(dim => {
              const val = xp.dimensions[dim.key] || 0
              const pct = Math.min(100, (val / 500) * 100)
              return (
                <div key={dim.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 9, width: 28, fontWeight: 700, color: dim.color, fontVariantNumeric: 'tabular-nums' }}>
                    {dim.label}
                  </span>
                  <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--bg-elevated, #21262d)' }}>
                    <div style={{
                      width: `${pct}%`, height: '100%', borderRadius: 2,
                      background: `linear-gradient(90deg, ${dim.color}cc, ${dim.color})`,
                    }} />
                  </div>
                  <span style={{ fontSize: 9, width: 24, textAlign: 'right', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {val}
                  </span>
                </div>
              )
            })}
          </div>
        )}

        {/* Description (condensed) */}
        {!xp && (
          <div style={{
            fontSize: 12, lineHeight: 1.5,
            color: 'var(--text-secondary, #c9d1d9)',
            marginBottom: 10,
          }}>
            {persona.description}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => { window.location.href = homeUrl }}
            style={{
              fontSize: 11, color: persona.color, fontWeight: 500,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
            onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
          >
            Visit Home →
          </button>
          {xp && (
            <button
              onClick={() => { window.location.href = `/evolve/persona/${persona.name}` }}
              style={{
                fontSize: 11, color: '#856FC8', fontWeight: 500,
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              }}
              onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
            >
              Growth Details →
            </button>
          )}
        </div>
      </div>

      {/* Animation keyframes injected once */}
      <style>{`
        @keyframes personaPopupEnter {
          from { opacity: 0; transform: translateY(6px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  )
})
